import ControlledTextField from "@/app/(browse)/sell/ad/form/ControlledTextField";
import { Box, Typography } from "@mui/material";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import PillButton from "../PillButton";
import { useContactDetails } from "@/app/layout/useContactDetails";
import { useEffect } from "react";
import { CreateInsuranceCase } from "@/case/actions/CreateInsuranceCase";
import ControlledSelect from "@/app/(browse)/sell/ad/form/ControlledSelect";
import ControlledSuburbField from "@/app/(browse)/sell/ad/form/ControlledSuburbField";
import { AD_SUBURB_DEFAULTS } from "@/sell/models/AdSuburb";
import {
  CREATE_INSURANCE_CASE_VIEW_MODEL_SCHEMA,
  CreateInsuranceCaseViewModel,
  INSURANCE_AGE_OPTIONS,
  InsuranceCaseSource,
} from "@/case/models/CreateInsuranceCaseViewModel";
import { zodResolver } from "@hookform/resolvers/zod";
import { AdInsuranceModel } from "@/models/AdInsuranceModel";

export function InsuranceLeadForm({
  insurance,
  source
}: {
  insurance: AdInsuranceModel;
  source: InsuranceCaseSource;
}) {
  const { contactDetails } = useContactDetails();
  const methods = useForm<CreateInsuranceCaseViewModel>({
    resolver: zodResolver(CREATE_INSURANCE_CASE_VIEW_MODEL_SCHEMA),
    defaultValues: {
      key: insurance.adId,
      name: "",
      email: "",
      phone: "",
      countryCode: "AU",
      ageOfHorse: insurance.age,
      suburb: AD_SUBURB_DEFAULTS,
      source: source,
    },
  });

  useEffect(() => {
    methods.setValue("name", contactDetails?.name || "");
    methods.setValue("email", contactDetails?.email || "");
    methods.setValue("phone", contactDetails?.phone || "");
  }, [contactDetails, methods]);

  const onSubmit: SubmitHandler<CreateInsuranceCaseViewModel> = async (
    data,
  ) => {
    await CreateInsuranceCase(data);
    methods.reset();
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={methods.handleSubmit(onSubmit)}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {methods.formState.isSubmitSuccessful ? (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Typography variant="h6">Thank you.</Typography>
              <Typography>
                Petcover will be in touch shortly with your insurance quote.
              </Typography>
            </Box>
          </Box>
        ) : (
          <>
            <ControlledTextField name="name" label="Name" required />
            <ControlledTextField name="phone" label="Phone" required />
            <ControlledTextField name="email" label="Email" required />
            <ControlledSelect
              name="ageOfHorse"
              label="Age of Horse"
              options={INSURANCE_AGE_OPTIONS}
              required
            />
            <ControlledSuburbField
              name="suburb"
              label="Town / Suburb (Australia only)"
              countryName="countryCode"
            />
            <PillButton
              primary
              sx={{
                alignSelf: "end",
              }}
              type="submit"
              loading={methods.formState.isSubmitting}
              disabled={methods.formState.isSubmitting}
            >
              Get a Quote
            </PillButton>
          </>
        )}
      </Box>
    </FormProvider>
  );
}
